import type { CalcType } from "../../../mocks/calculatorData";

interface CalcTipsProps {
  calcType: CalcType;
}

const homeTips = [
  {
    emoji: "⚖️",
    color: "#FEF3C7",
    accentColor: "#f59e0b",
    title: "Volume, not weight",
    body: "We charge by the cubic yard of space your items take up — not by how heavy they are. A big light sofa costs more than a small heavy microwave!",
  },
  {
    emoji: "⚠️",
    color: "#FEE2E2",
    accentColor: "#ef4444",
    title: "Special item surcharges",
    body: "Fridges, mattresses and TVs need specialist recycling so carry a small extra charge on top of the load price. We'll show you the full breakdown at booking.",
  },
  {
    emoji: "💡",
    color: "#6CC412",
    accentColor: "#6CC412",
    title: "Only pay for what we take",
    body: "Not sure if you have a full load? Choose our Flexible Load and you only pay per cubic yard actually collected. No over-paying, ever.",
  },
];

const officeTips = [
  {
    emoji: "🌙",
    color: "#EDE9FE",
    accentColor: "#8b5cf6",
    title: "Off-hours clearance",
    body: "Office clearances can be done overnight or at weekends to avoid disrupting your team. Just pick your preferred window at booking — no extra charge.",
  },
  {
    emoji: "📋",
    color: "#6CC412",
    accentColor: "#6CC412",
    title: "Waste transfer notes",
    body: "Every commercial collection comes with a full waste transfer note for your records and compliance requirements. We're fully licensed by the Environment Agency.",
  },
  {
    emoji: "🖥️",
    color: "#FEF3C7",
    accentColor: "#f59e0b",
    title: "IT & WEEE disposal",
    body: "Servers, monitors and printers are disposed of as WEEE waste. We provide disposal certificates on request — just mention it when booking.",
  },
];

export default function CalcTips({ calcType }: CalcTipsProps) {
  const tips = calcType === "home" ? homeTips : officeTips;

  return (
    <div className="mt-12">
      <div className="flex items-center gap-3 mb-6">
        <span className="text-2xl">💬</span>
        <p className="font-black text-cl-dark text-lg">Good to know before you book</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {tips.map((tip) => (
          <div
            key={tip.title}
            className="rounded-3xl p-5 thick-border hover:cartoon-shadow transition-all duration-200"
            style={{ background: tip.color }}
          >
            <div className="text-3xl mb-3">{tip.emoji}</div>
            <div
              className="inline-block w-1 h-5 rounded-full mb-3"
              style={{ background: tip.accentColor }}
            ></div>
            <p className="font-black text-cl-dark text-sm mb-2">{tip.title}</p>
            <p className="text-xs text-cl-dark/60 font-semibold leading-relaxed">{tip.body}</p>
          </div>
        ))}
      </div>

      {/* Fun bottom CTA */}
      <div
        className="mt-6 rounded-3xl p-6 thick-border flex flex-col sm:flex-row items-center gap-5"
        style={{ background: "#09245b" }}
      >
        <div className="flex-1 text-center sm:text-left">
          <p className="text-white font-black text-base mb-1">
            Still not sure? Just give us a call 📞
          </p>
          <p className="text-white/50 text-sm font-semibold">
            Our team will figure it out with you in 2 minutes — no obligation.
          </p>
        </div>
        <a
          href="tel:03335775445"
          className="flex items-center gap-2 px-6 py-3 rounded-full font-black text-cl-dark whitespace-nowrap cursor-pointer transition-all thick-border cartoon-shadow-sm hover:cartoon-shadow"
          style={{ background: "#6CC412" }}
        >
          <i className="ri-phone-fill text-sm"></i>
          03335 775445
        </a>
      </div>
    </div>
  );
}
